import React from 'react';

import { useEnvironment, useTranslation, useBi } from '@wix/yoshi-flow-editor';
import { memberWebAppButtonClick as memberWebAppButtonClickV2 } from '@wix/bi-logger-challenges-member-web/v2';
import { useSettings } from '@wix/tpa-settings/react';
import challengeSettings from '../../../settingsParams';
import { ButtonNames } from '../../../../../contexts/main/biInterfaces';

import { convertTextAlignmentToPopoverPlacement } from '../../../../../services/settingsHelpers';
import { Dots } from '../Dots';
import { classes, st } from './ChallengeDots.st.css';
import utils from '../../views/utils';
import { dataHooks } from '@wix/data-hooks';
import {
  useCSSPBEnabled,
  useCSSPBStyle,
} from '../../../../../hooks/useCSSPBStyle';
import { useGeneralData } from '../../../../../contexts/GeneralDataProvider/GeneralDataContext';
import { useChallengeData } from '../../../../../contexts/storage-contexts/Challenge';
import { useLocation } from '../../../../../contexts/Location/LocationContext';
import { TextAlignment } from '../../../../ChallengesPage/Settings/challengeSettings/challengeSettings.types';
import { getInviteLink } from '../../../../../utils/getInviteLink';

export interface IChallengeDotsProps {
  className?: string;
}

export const challengeDotsDataHooks = dataHooks<{
  main;
}>('challenge-dots');

export const ChallengeDots: React.FunctionComponent<IChallengeDotsProps> = ({
  className = '',
}: IChallengeDotsProps) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const alignment = utils.getHeaderAlignByType(settings);
  const { baseUrl } = useLocation();
  const {
    challengeData: { challenge },
  } = useChallengeData();
  const bi = useBi();
  const { isParticipantPageAvailable } = useGeneralData();
  const getCSSPBStyle = useCSSPBStyle();

  const cssPBEnabled = useCSSPBEnabled();
  const isDisplayOneAppSettingEnabled = settings.get(
    challengeSettings.joinViaOneAppForParticipantIsEnabled,
  );
  const showOneApp = cssPBEnabled || isDisplayOneAppSettingEnabled;

  if (!showOneApp) {
    // Now we have only one menu item, so the entire menu should be hidden, if this menu item isn't visible.
    return null;
  }
  const inviteLink = getInviteLink({
    baseUrl,
    programId: challenge.id,
  });
  return (
    <div
      className={`${st(classes.root, {
        mobile: isMobile,
        newParticipantPage: isParticipantPageAvailable,
        alignment,
      })} ${className}`}
      style={getCSSPBStyle({
        displayVar: '--opgJoinViaOneAppForParticipantIsEnabled',
      })}
      data-hook={challengeDotsDataHooks.main()}
    >
      <Dots
        menuItems={[
          {
            title: t('challenge.page.join-via', {
              wixApp: t('challenge.wix-app'),
            }),
            isHidden: !showOneApp,
            callback: async () => {
              await bi.report(
                memberWebAppButtonClickV2({
                  buttonName: ButtonNames.GoToOneApp,
                }),
              );

              window.open(inviteLink, '_blank');
            },
          },
        ]}
        popoverPlacement={convertTextAlignmentToPopoverPlacement(
          alignment as TextAlignment,
        )}
        onOpenMenu={async () => {
          await bi.report(
            memberWebAppButtonClickV2({
              buttonName: ButtonNames.OpenDotsMenu,
            }),
          );
        }}
      />
    </div>
  );
};
ChallengeDots.displayName = 'ChallengeDots';
